import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TmProjectView } from "../../api/tm";

type Props = {
  projects: TmProjectView[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  /** When true the picker is read-only — used when the task is being added from a project's "Add task" CTA. */
  disabled?: boolean;
};

export function ProjectCombobox({ projects, value, onChange, placeholder, disabled }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => projects.find((p) => p.id === value) ?? null, [projects, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects.slice(0, 50);
    return projects.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 50);
  }, [projects, query]);

  useEffect(() => {
    function close(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    // 'click' rather than 'mousedown' — pick runs in the option's click
    // event, and a mousedown listener would tear down the dropdown before
    // the selection commits.
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  useEffect(() => { setActiveIdx(0); }, [query, open]);

  function pick(p: TmProjectView) { onChange(p.id); setQuery(""); setOpen(false); }
  function clear() { onChange(null); setQuery(""); inputRef.current?.focus(); }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && open && filtered[activeIdx]) { e.preventDefault(); pick(filtered[activeIdx]); }
    else if (e.key === "Escape") setOpen(false);
  }

  const ph = placeholder ?? t("tm.projectCombobox.placeholder", "— pick project —");

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div
        // No onClick on the wrapper — option clicks were bubbling here and
        // re-opening the dropdown right after pick closed it, leaving the
        // box in "open + placeholder" state. Opening now happens via the
        // input's onFocus.
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6,
          background: disabled ? "var(--surface-2)" : "var(--surface)",
          cursor: disabled ? "not-allowed" : "text",
          opacity: disabled ? 0.8 : 1,
        }}
      >
        {selected && !open ? (
          <span style={{ flex: 1, fontSize: 14 }}>
            <b>{selected.name}</b>
            {selected.teamName ? <span style={{ color: "var(--ink-3)" }}> · {selected.teamName}</span> : null}
          </span>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={query}
            disabled={disabled}
            onFocus={() => !disabled && setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onKeyDown={onKey}
            placeholder={selected ? selected.name : ph}
            style={{ flex: 1, border: "none", outline: "none", fontSize: 14, fontFamily: "inherit", background: "transparent" }}
            aria-autocomplete="list" aria-expanded={open} role="combobox"
          />
        )}
        {selected && (
          <button type="button" onClick={(e) => { e.stopPropagation(); clear(); }}
                  aria-label={t("action.clear", "Clear")}
                  style={{ background: "transparent", border: "none", color: "var(--ink-3)", fontSize: 16, cursor: "pointer", padding: 2 }}>
            ×
          </button>
        )}
        <span style={{ color: "var(--ink-4)", fontSize: 12 }} aria-hidden>▾</span>
      </div>
      {open && (
        <div role="listbox" className="dd-scroll" style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          maxHeight: 280, overflowY: "auto", background: "var(--surface)",
          border: "1px solid var(--border)", borderRadius: 6,
          boxShadow: "var(--shadow-2)", zIndex: 20,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 10, fontSize: 13, color: "var(--ink-3)" }}>
              {t("common.empty", "No matches.")}
            </div>
          ) : filtered.map((p, i) => (
            <div
              key={p.id} role="option" aria-selected={p.id === value}
              onMouseEnter={() => setActiveIdx(i)}
              // mousedown + preventDefault: commit before any blur/click race
              // tears the dropdown down or refocuses the wrapper.
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); pick(p); }}
              style={{
                padding: "8px 10px", cursor: "pointer", fontSize: 14,
                background: i === activeIdx ? "var(--tone-indigo-bg)" : p.id === value ? "var(--surface-2)" : "transparent",
              }}>
              <div style={{ fontWeight: 600 }}>{p.name}</div>
              {p.teamName && <div style={{ fontSize: 13, color: "var(--ink-3)" }}>{p.teamName}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
